import { useCallback, useSyncExternalStore } from "react";

import type { Locale, TranslationParams } from "@/src/i18n/translate";
import { DEFAULT_LOCALE, isLocale, translate } from "@/src/i18n/translate";
import type { Scope, Translations } from "@/src/i18n/translations";
import { writeStorage } from "@/src/libs/storage";

/** Clé de stockage — lue aussi par le script d'amorçage d'`index.html`. */
const STORAGE_KEY = "language";

/**
 * Langue de l'admin.
 *
 * **La source de vérité est le DOM** — l'attribut `lang` de `<html>` — exactement
 * comme le thème l'est par la classe `dark` (cf. `src/hooks/useTheme.ts`). Ce
 * n'est pas un caprice de symétrie :
 *
 * - `lang` **doit** de toute façon suivre la langue affichée, sinon les lecteurs
 *   d'écran prononcent le français avec une phonétique anglaise et la correction
 *   orthographique du navigateur se trompe de dictionnaire. Le tenir à jour en
 *   plus d'un état React reviendrait à maintenir deux copies de la même
 *   information, qui finiraient par diverger ;
 * - ça évite un provider de plus dans `App.tsx`, dont l'ordre est contraint.
 *
 * Conséquence à connaître : la langue mémorisée est appliquée par le script
 * inline d'`index.html`, avant le premier rendu. Ici on ne fait que la lire —
 * et l'écrire quand l'utilisateur en change.
 */
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Locale {
  const lang = document.documentElement.lang;
  return isLocale(lang) ? lang : DEFAULT_LOCALE;
}

/** Fixe la langue et la mémorise. */
export function setLocale(locale: Locale) {
  writeStorage(STORAGE_KEY, locale);
  document.documentElement.lang = locale;
  emit();
}

export function useLocale() {
  const locale = useSyncExternalStore(subscribe, getSnapshot);

  return { locale, setLocale };
}

/**
 * Traducteur d'un scope — le hook que les composants utilisent.
 *
 * ```ts
 * const t = useTranslations("signIn");
 * t("title");
 * ```
 *
 * La clé est **typée par le scope** : `t("titre")` ne compile pas, et renommer
 * une entrée du dictionnaire fait échouer `tsc` partout où elle était utilisée.
 */
export function useTranslations<S extends Scope>(scope: S) {
  const { locale } = useLocale();

  return useCallback(
    (key: keyof Translations[S], params?: TranslationParams) =>
      translate(locale, scope, key, params),
    [locale, scope],
  );
}
